/** /join (enter a code) and /j/:code (name, avatar, role, then play or watch). */
import { isEmailLike } from "@shared/participants";
import { AVATARS, isSessionCode, type JoinInfo, type JoinResponse, MAX_NAME_LENGTH } from "@shared/protocol";
import { useEffect, useMemo, useState } from "preact/hooks";
import { RoleBadge } from "../components/game";
import { Header, Page } from "../components/Header";
import { hasIcon, Icon } from "../components/Icon";
import { Button, Card, Field, Pill, Spinner } from "../components/ui";
import { useI18n, useT } from "../i18n";
import { type ApiError, get, post } from "../lib/api";
import { loadJoin, saveJoin } from "../lib/session-store";
import { navigate } from "../router";

function normalize(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .replaceAll("0", "O")
    .replaceAll("1", "I")
    .slice(0, 6);
}

export function JoinEntry() {
  const t = useT();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = (e: Event) => {
    e.preventDefault();
    const c = normalize(code);
    if (!isSessionCode(c)) {
      setError(t("join.invalidCode"));
      return;
    }
    navigate(`/j/${c}`);
  };
  return (
    <Page>
      <Header />
      <main id="main" class="narrow entry">
        <Card class="stack">
          <h1>{t("join.title")}</h1>
          <form onSubmit={submit} class="stack">
            <Field label={t("join.codeLabel")} htmlFor="code" error={error}>
              <input
                id="code"
                class={`input code ${error ? "error" : ""}`}
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellcheck={false}
                maxLength={8}
                placeholder={t("join.codePlaceholder")}
                value={code}
                onInput={(e) => {
                  setCode((e.currentTarget as HTMLInputElement).value);
                  setError(null);
                }}
              />
            </Field>
            <Button size="lg" block icon="arrow-right" type="submit">
              {t("join.enter")}
            </Button>
          </form>
        </Card>
      </main>
    </Page>
  );
}

export function JoinSession({ code, query }: { code: string; query: URLSearchParams }) {
  const { t, locale } = useI18n();
  const normalized = normalize(code);
  const seat = query.get("seat");
  const [info, setInfo] = useState<JoinInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<string>(() => AVATARS[Math.floor(Math.random() * AVATARS.length)]!);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const saved = useMemo(() => loadJoin(normalized), [normalized]);

  useEffect(() => {
    if (!isSessionCode(normalized)) {
      setError(t("join.invalidCode"));
      return;
    }
    let cancelled = false;
    get<JoinInfo>(
      `/api/join/${normalized}?lang=${encodeURIComponent(locale)}${seat ? `&seat=${encodeURIComponent(seat)}` : ""}`,
    )
      .then((i) => {
        if (cancelled) return;
        setInfo(i);
        if (i.seat) {
          setRoleId(i.seat.roleId);
          setName((n) => n || i.seat!.label);
        }
      })
      .catch((e: ApiError) => {
        if (cancelled) return;
        setError(
          e.status === 404 ? t("join.notFound") : e.status === 410 ? t("join.ended") : t("error.generic"),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [normalized, seat, locale]);

  // A returning phone goes straight back in.
  useEffect(() => {
    if (saved && info && info.status !== "ended") navigate(`/play/${normalized}`, true);
  }, [saved, info, normalized]);

  const doJoin = async (kind: "player" | "viewer") => {
    if (!info) return;
    const address = email.trim();
    if (address && !isEmailLike(address)) {
      setEmailError(t("join.emailInvalid"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await post<JoinResponse>(`/api/join/${normalized}`, {
        name: name.trim(),
        avatar,
        email: address || null,
        roleId: kind === "player" ? roleId : null,
        seat: kind === "player" ? seat : null,
        kind,
        token: saved?.token ?? null,
      });
      saveJoin({
        code: normalized,
        token: res.token,
        playerId: res.playerId,
        kind: res.kind,
        wsPath: res.wsPath,
        name: res.name,
        roleId: res.roleId,
        savedAt: Date.now(),
      });
      navigate(`/play/${normalized}`);
    } catch (e) {
      const err = e as ApiError;
      const map: Record<string, string> = {
        ended: t("join.ended"),
        full: t("join.full"),
        roleFull: t("join.roleFull"),
        rateLimited: t("join.rateLimited"),
        notFound: t("join.notFound"),
        network: t("error.network"),
      };
      setError(map[err.code] ?? t("join.error", { message: err.message }));
      setBusy(false);
    }
  };

  if (error && !info) {
    return (
      <Page>
        <Header />
        <main id="main" class="narrow entry">
          <Card class="stack">
            <h1>{t("join.title")}</h1>
            <p role="alert" class="error-text">
              {error}
            </p>
            <Button variant="secondary" onClick={() => navigate("/join")}>
              {t("action.back")}
            </Button>
          </Card>
        </main>
      </Page>
    );
  }

  if (!info) {
    return (
      <Page>
        <Header />
        <main id="main" class="entry">
          <Spinner label={t("join.loading")} />
        </main>
      </Page>
    );
  }

  const rolesOpen = info.settings.openRoleSelection && !info.seat;

  return (
    <Page>
      <Header />
      <main id="main" class="narrow entry stack">
        <div>
          <span class="eyebrow">{t("join.title")}</span>
          <h1 style={{ marginBottom: 4 }}>{info.title}</h1>
          <p class="fg-2">
            {info.scenario.tagline} · {t("join.minutes", { count: info.scenario.estimatedMinutes })}
          </p>
          <p class="muted small">{t("join.playersIn", { count: info.playerCount })}</p>
        </div>

        {info.seat ? (
          <Card class="signal stack-sm">
            <strong>{t("join.seatTitle")}</strong>
            <p class="small" style={{ margin: 0 }}>
              {t("join.seatBody", { label: info.seat.label })}
            </p>
            <RoleBadge role={info.roles.find((r) => r.id === info.seat?.roleId)} />
          </Card>
        ) : null}

        <Card class="stack">
          <Field label={t("join.nameLabel")} htmlFor="name">
            <input
              id="name"
              class="input"
              maxLength={MAX_NAME_LENGTH}
              autoComplete="nickname"
              placeholder={t("join.namePlaceholder")}
              value={name}
              onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
            />
          </Field>
          <Field
            label={t("join.emailLabel")}
            htmlFor="email"
            help={info.seat?.hasEmail ? t("join.emailHelpSeat") : t("join.emailHelp")}
            error={emailError}
          >
            <input
              id="email"
              class={`input ${emailError ? "error" : ""}`}
              type="email"
              autoComplete="email"
              inputMode="email"
              spellcheck={false}
              maxLength={254}
              placeholder={t("join.emailPlaceholder")}
              value={email}
              onInput={(e) => {
                setEmail((e.currentTarget as HTMLInputElement).value);
                setEmailError(null);
              }}
            />
          </Field>
          <div class="field">
            <div class="label">{t("join.avatarLabel")}</div>
            <div class="avatar-pick" role="radiogroup" aria-label={t("join.avatarLabel")}>
              {AVATARS.map((a) => (
                <button
                  key={a}
                  type="button"
                  role="radio"
                  aria-checked={avatar === a}
                  aria-label={a}
                  onClick={() => setAvatar(a)}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
          {rolesOpen ? (
            <div class="field">
              <div class="label">{t("join.roleLabel")}</div>
              <div class="help">{t("join.roleHelp")}</div>
              <div class="choice-list" role="radiogroup" aria-label={t("join.roleLabel")}>
                <button
                  type="button"
                  role="radio"
                  class="choice"
                  aria-checked={roleId === null}
                  onClick={() => setRoleId(null)}
                >
                  <Icon name="users" />
                  <div class="title">{t("join.roleAny")}</div>
                </button>
                {info.roles.map((r) => {
                  const full = r.max !== null && r.taken >= r.max;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      role="radio"
                      class="choice"
                      aria-checked={roleId === r.id}
                      disabled={full}
                      onClick={() => setRoleId(r.id)}
                    >
                      <Icon name={hasIcon(r.icon) ? r.icon : "user"} />
                      <div class="grow">
                        <div class="title">{r.name}</div>
                        <div class="sub">
                          {r.max !== null
                            ? full
                              ? t("join.roleFull")
                              : t("join.roleTaken", { taken: r.taken, max: r.max })
                            : t("join.roleCount", { count: r.taken })}
                        </div>
                      </div>
                      {full ? <Pill>{t("join.roleFull")}</Pill> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {error ? (
            <p role="alert" class="error-text">
              {error}
            </p>
          ) : null}
          <p class="muted xs" style={{ margin: 0 }}>
            {t("join.privacyNote")}
          </p>
          <Button
            size="lg"
            block
            icon="play"
            disabled={busy || info.status === "ended"}
            onClick={() => doJoin("player")}
          >
            {busy ? t("join.joining") : t("join.asPlayer")}
          </Button>
          {info.settings.allowViewers ? (
            <Button
              size="md"
              block
              variant="ghost"
              icon="eye"
              disabled={busy}
              onClick={() => doJoin("viewer")}
            >
              {t("join.asViewer")}
            </Button>
          ) : null}
        </Card>
      </main>
    </Page>
  );
}

/** /watch/:code — join silently as a viewer and go to the play view. */
export function WatchSession({ code }: { code: string }) {
  const t = useT();
  const normalized = normalize(code);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const saved = loadJoin(normalized);
    if (saved?.kind === "viewer") {
      navigate(`/play/${normalized}`, true);
      return;
    }
    post<JoinResponse>(`/api/join/${normalized}`, { kind: "viewer", token: saved?.token ?? null })
      .then((res) => {
        saveJoin({
          code: normalized,
          token: res.token,
          playerId: res.playerId,
          kind: "viewer",
          wsPath: res.wsPath,
          name: res.name,
          roleId: null,
          savedAt: Date.now(),
        });
        navigate(`/play/${normalized}`, true);
      })
      .catch((e: ApiError) =>
        setError(
          e.status === 404 ? t("join.notFound") : e.status === 410 ? t("join.ended") : t("error.generic"),
        ),
      );
  }, [normalized]);
  return (
    <Page>
      <Header />
      <main id="main" class="entry">
        {error ? (
          <p role="alert" class="error-text">
            {error}
          </p>
        ) : (
          <Spinner label={t("join.loading")} />
        )}
      </main>
    </Page>
  );
}
